'use client';

import { useKeenSlider } from 'keen-slider/react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { cn, getVideoThumbnailUrl } from '../../lib/utils';
import VideoJS from '../VideoJS';

type NeoCarouselProps = {
  images: { type: 'image' | 'video' | 'gif'; url: string }[]; // Array of image URLs
  className: string;
  full?: boolean; // If true, image will be full size, otherwise max-h-[512px]
};

export default function NeoCarousel({ images, className, full = false }: NeoCarouselProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    slideChanged(slider) {
      if (instanceRef.current) {
        setCurrentSlide(slider.track.details.rel);
      }
    },
    created() {
      setLoaded(true);
    },
  });

  useEffect(() => {
    if (loaded && instanceRef.current) {
      instanceRef.current.update();
    }
  }, [loaded, instanceRef]);

  return (
    <div className="relative size-full">
      <div ref={sliderRef} className="keen-slider size-full">
        {images.map((media, index) => (
          <div key={index} className="keen-slider__slide flex min-h-64 max-h-[512px] items-center justify-center">
            {media.type === 'image' ? (
              <img
                src={media.url}
                alt={`Slide ${index + 1}`}
                className={cn(
                  'size-full bg-black',
                  !full ? 'max-h-[512px]' : 'object-contain',
                  className,
                )}
              />) : (

              <VideoJS
                className='size-full'
                displayVolumeButton={true}
                options={{
                  autoplay: true,
                  playsinline: true,
                  controls: false,
                  poster: getVideoThumbnailUrl(media.url),
                  preload: 'auto',
                  loop: true,
                  fluid: true,
                  fill: false,
                  responsive: true,
                  sources: [{
                    src: media.url,
                    type: 'application/x-mpegURL',
                  }],
                }}
              />
            )}
          </div>
        ))}
      </div>
      {loaded && instanceRef.current && instanceRef.current.track && (
        <>
          {currentSlide !== 0 && (
            <Arrow
              left
              onClick={(e: any) =>
                e.stopPropagation() || instanceRef.current?.prev()}
            />
          )}

          {currentSlide !== instanceRef.current.track.details.slides.length - 1 && (
            <Arrow
              onClick={(e: any) =>
                e.stopPropagation() || instanceRef.current?.next()}
            />
          )}
          {
            (images.length > 1)
              ? (
                <div className="absolute inset-x-0 bottom-4">
                  <div className="flex justify-center gap-2">
                    {[...Array.from({ length: images.length })].map((_, idx) => (
                      <button
                        type="button"
                        key={idx}
                        onClick={() => {
                          instanceRef.current?.moveToIdx(idx);
                        }}
                        className={cn(
                          'size-2 rounded-full transition-all',
                          currentSlide === idx ? 'bg-white' : 'bg-white/50',
                        )}
                      />
                    ))}
                  </div>
                </div>
              )
              : ''
          }
        </>
      )}
    </div>
  );
}

function Arrow({ left, onClick }: { left?: boolean; onClick: (e: any) => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`absolute top-1/2 hidden -translate-y-1/2 rounded-full bg-white/50 p-1 transition-all duration-300 hover:bg-opacity-75 md:block ${left ? 'left-2' : 'right-2'
        }`}
      aria-label={left ? 'Previous slide' : 'Next slide'}
    >
      {left
        ? (
          <ChevronLeft className="size-6 text-gray-800" />
        )
        : (
          <ChevronRight className="size-6 text-gray-800" />
        )}
    </button>
  );
}
